import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import './tour.css';

const PADDING = 8;
const GAP = 14;
const VIEWPORT_MARGIN = 16;
const POPOVER_WIDTH = 340;
const POPOVER_HEIGHT_ESTIMATE = 200;

const findTarget = (selector) => {
	if (!selector) return null;
	try {
		const els = document.querySelectorAll(selector);
		for (const el of els) {
			const rect = el.getBoundingClientRect();
			if (rect.width > 0 && rect.height > 0) return el;
		}
		return null;
	} catch (e) { return null; }
};

const computeLayout = (rect) => {
	if (!rect) return null;
	const vw = window.innerWidth;
	const vh = window.innerHeight;

	// Clamp the spotlight to viewport bounds so very tall targets don't
	// extend past it (which would leave the overlay un-darkened for content
	// below the target).
	const top = Math.max(rect.top - PADDING, 0);
	const bottom = Math.min(rect.bottom + PADDING, vh);
	const left = Math.max(rect.left - PADDING, 0);
	const right = Math.min(rect.right + PADDING, vw);

	const spotTop = top;
	const spotLeft = left;
	const spotWidth = Math.max(0, right - left);
	const spotHeight = Math.max(0, bottom - top);

	const spaceBelow = vh - (spotTop + spotHeight);
	const spaceAbove = spotTop;
	const placeBelow = spaceBelow > POPOVER_HEIGHT_ESTIMATE + GAP || spaceAbove < POPOVER_HEIGHT_ESTIMATE + GAP;
	const popTop = placeBelow
		? Math.min(spotTop + spotHeight + GAP, vh - POPOVER_HEIGHT_ESTIMATE - VIEWPORT_MARGIN)
		: Math.max(spotTop - GAP - POPOVER_HEIGHT_ESTIMATE, VIEWPORT_MARGIN);

	const targetCenterX = spotLeft + spotWidth / 2;
	let popLeft = targetCenterX - POPOVER_WIDTH / 2;
	popLeft = Math.max(VIEWPORT_MARGIN, Math.min(popLeft, vw - POPOVER_WIDTH - VIEWPORT_MARGIN));

	return {
		spotTop, spotLeft, spotWidth, spotHeight,
		popTop, popLeft,
		placement: placeBelow ? 'below' : 'above',
	};
};

const Tour = ({ steps, open, onClose }) => {
	const { t } = useTranslation();
	const [stepIndex, setStepIndex] = useState(0);
	const [layout, setLayout] = useState(null);
	const [validSteps, setValidSteps] = useState([]);

	const handleClose = useCallback((finished) => {
		onClose?.(finished);
	}, [onClose]);

	// When the tour opens, filter out steps whose target isn't currently in the DOM
	// (e.g. conditionally rendered sections like Score that don't apply to this payload).
	useEffect(() => {
		if (!open) {
			setValidSteps([]);
			setStepIndex(0);
			return undefined;
		}
		const id = window.setTimeout(() => {
			const filtered = steps.filter((s) => !!findTarget(s.target));
			if (filtered.length === 0) {
				handleClose(true);
				return;
			}
			setValidSteps(filtered);
			setStepIndex(0);
		}, 50);
		return () => window.clearTimeout(id);
	}, [open, steps, handleClose]);

	const next = useCallback(() => {
		setStepIndex((i) => {
			if (i + 1 >= validSteps.length) {
				handleClose(true);
				return 0;
			}
			return i + 1;
		});
	}, [validSteps.length, handleClose]);

	const prev = useCallback(() => {
		setStepIndex((i) => Math.max(0, i - 1));
	}, []);

	useLayoutEffect(() => {
		if (!open) return undefined;
		const step = validSteps[stepIndex];
		if (!step) return undefined;

		const compute = () => {
			const el = findTarget(step.target);
			if (!el) {
				setLayout(null);
				return;
			}
			setLayout(computeLayout(el.getBoundingClientRect()));
		};

		const el = findTarget(step.target);
		if (el && typeof el.scrollIntoView === 'function') {
			// If the target is taller than the viewport, align its top instead
			// of centering so the spotlight starts at the section header.
			const rect = el.getBoundingClientRect();
			const block = rect.height > window.innerHeight * 0.7 ? 'start' : 'center';
			el.scrollIntoView({ block, behavior: 'smooth' });
		}

		compute();
		const reCompute = () => compute();
		window.addEventListener('resize', reCompute);
		window.addEventListener('scroll', reCompute, true);
		const interval = window.setInterval(compute, 400);
		return () => {
			window.removeEventListener('resize', reCompute);
			window.removeEventListener('scroll', reCompute, true);
			window.clearInterval(interval);
		};
	}, [open, stepIndex, validSteps]);

	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e) => {
			if (e.key === 'Escape') handleClose(false);
			else if (e.key === 'ArrowRight') next();
			else if (e.key === 'ArrowLeft') prev();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open, handleClose, next, prev]);

	const step = validSteps[stepIndex];
	const isLast = stepIndex === validSteps.length - 1;

	const content = useMemo(() => {
		if (!open || !step) return null;
		if (!layout) {
			return <div className='ds-tour-backdrop' onClick={() => handleClose(false)} aria-hidden='true' />;
		}

		return (
			<>
				<div
					className='ds-tour-spotlight'
					style={{
						top: layout.spotTop,
						left: layout.spotLeft,
						width: layout.spotWidth,
						height: layout.spotHeight,
					}}
					aria-hidden='true'
				/>
				<div
					className={`ds-tour-popover ds-tour-popover--${layout.placement}`}
					style={{ top: layout.popTop, left: layout.popLeft, width: POPOVER_WIDTH }}
					role='dialog'
					aria-labelledby='ds-tour-title'
				>
					<div className='ds-tour-popover-head'>
						<span className='ds-tour-step'>{stepIndex + 1} / {validSteps.length}</span>
						<button
							type='button'
							className='ds-tour-close'
							onClick={() => handleClose(false)}
							aria-label='Close tour'
						>×</button>
					</div>
					<h3 id='ds-tour-title' className='ds-tour-title'>{step.title}</h3>
					{step.body && <p className='ds-tour-body'>{step.body}</p>}
					<div className='ds-tour-actions'>
						<button type='button' className='ds-tour-skip' onClick={() => handleClose(true)}>
							{step.skipLabel || t('tour.skip', { defaultValue: 'Skip' })}
						</button>
						<div className='ds-tour-nav'>
							{stepIndex > 0 && (
								<button type='button' className='ds-tour-btn ds-tour-btn--ghost' onClick={prev}>
									{step.backLabel || t('tour.back', { defaultValue: 'Back' })}
								</button>
							)}
							<button type='button' className='ds-tour-btn ds-tour-btn--primary' onClick={isLast ? () => handleClose(true) : next}>
								{isLast ? (step.doneLabel || t('tour.done', { defaultValue: 'Got it' })) : (step.nextLabel || t('tour.next', { defaultValue: 'Next' }))}
							</button>
						</div>
					</div>
				</div>
			</>
		);
	}, [open, step, layout, stepIndex, validSteps.length, handleClose, prev, next, isLast, t]);

	if (!open || typeof document === 'undefined') return null;
	return createPortal(content, document.body);
};

Tour.propTypes = {
	steps: PropTypes.arrayOf(PropTypes.shape({
		target: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		body: PropTypes.node,
	})).isRequired,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default Tour;
